import React from "react";
import { cn } from "../../lib/utils";
import { StatValueSkeleton } from "./Skeleton";

export interface PageHeaderStat {
    label: string;
    value: string | number;
    icon?: React.ReactNode;
    color?: string; // tailwind class
    description?: string | React.ReactNode;
}

export interface PageHeaderProps {
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    actions?: React.ReactNode;
    stats?: PageHeaderStat[];
    loading?: boolean;
    statLabels?: { label: string; icon?: React.ReactNode; color?: string }[]; // For showing structure while loading
    reverseOrder?: boolean; // If true, shows subtitle above title (for Home page)
    sticky?: boolean;
    className?: string;
    contentClassName?: string;
}

const PageHeader: React.FC<PageHeaderProps> = ({
    title,
    subtitle,
    actions,
    stats,
    loading = false,
    statLabels,
    reverseOrder = false,
    sticky = false,
    className,
    contentClassName,
}) => {
    return (
        <header
            className={cn(
                sticky
                    ? "sticky top-0 z-30 max-h-[100px] border-b border-gray-200 bg-background px-6 py-6 dark:border-gray-700"
                    : "mb-4",
                className,
            )}
        >
            <div className={cn(
                "flex flex-col gap-4 md:flex-row md:items-center md:justify-between",
                !sticky && "mb-5",
                contentClassName,
            )}>
                <div>
                    {reverseOrder ? (
                        <>
                            {subtitle && <p className="font-semibold text-[#6E7670] text-muted-foreground">{subtitle}</p>}
                            <h1 className="text-2xl font-bold text-[#004232] dark:text-white">{title}</h1>
                        </>
                    ) : (
                        <>
                            <h1 className="text-2xl font-bold text-[#004232] dark:text-white">{title}</h1>
                            {subtitle && <p className="font-semibold text-[#6E7670] text-muted-foreground">{subtitle}</p>}
                        </>
                    )}
                </div>
                {actions && <div>{actions}</div>}
            </div>
            {loading && statLabels && statLabels.length > 0 ? (
                // Show loading state with structure
                <div className={`grid gap-4 mb-2 ${statLabels.length === 2 ? 'grid-cols-1 sm:grid-cols-2' :
                        statLabels.length === 3 ? 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3' :
                            statLabels.length === 5 ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-5' :
                                statLabels.length === 6 ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' :
                                'grid-cols-1 sm:grid-cols-2 md:grid-cols-4'
                    }`}>
                    {statLabels.map((stat, idx) => (
                        <div
                            key={stat.label + idx}
                            className={`rounded-lg border border-gray-200 bg-white dark:bg-[#212936] dark:border-[#394150] p-4 relative ${stat.color || ''}`}
                        >
                            <div className="flex justify-between items-start">
                                <div className="flex-1">
                                    <div className="text-sm text-foreground mb-1">{stat.label}</div>
                                    <div className="text-2xl font-bold">
                                        <StatValueSkeleton />
                                    </div>
                                </div>
                                {stat.icon && (
                                    <div className="w-8 h-8 rounded-lg flex items-center justify-center ml-4 bg-[#F2FCFA] dark:bg-emerald-900/30">
                                        <div className="text-emerald-600 dark:text-emerald-400">{stat.icon}</div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            ) : stats && stats.length > 0 ? (
                // Show actual data
                <div className={`grid gap-4 mb-2 ${stats.length === 2 ? 'grid-cols-1 sm:grid-cols-2' :
                        stats.length === 3 ? 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3' :
                            stats.length === 5 ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-5' :
                                stats.length === 6 ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' :
                                'grid-cols-1 sm:grid-cols-2 md:grid-cols-4'
                    }`}>
                    {stats.map((stat, idx) => (
                        <div
                            key={stat.label + idx}
                            className={`rounded-lg border border-gray-200 bg-white dark:bg-[#212936] dark:border-[#394150] p-4 relative ${stat.color || ''}`}
                        >
                            <div className="flex justify-between items-start">
                                <div className="flex-1">
                                    <div className="text-2xl font-bold text-foreground">{stat.value}</div>
                                    <div className="text-sm text-foreground mb-1">{stat.label}</div>
                                    {stat.description && <div className="text-xs text-foreground mt-1">{stat.description}</div>}
                                </div>
                                {stat.icon && (
                                    <div className="w-8 h-8 rounded-lg flex items-center justify-center ml-4 bg-[#F2FCFA] dark:bg-emerald-900/30">
                                        <div className="text-emerald-600 dark:text-emerald-400">{stat.icon}</div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            ) : null}
        </header>
    );
};

export default PageHeader; 
